/**
 * New console — the phone's bottom menu.
 *
 * The classic console's BottomMenu, rebuilt from this console's own parts: a row of tabs
 * docked to the foot of a phone, the way an installed app is driven, so the portal can be
 * wrapped as an app and keep the menu people already know. The tabs are the classic's,
 * by role:
 *  - a portfolio login: Home, Leads, Clinics, Users, Traffic, More;
 *  - a clinic admin: Home, Leads, Templates, Users, More.
 * Each tab is read from the rail's own item in navigation.jsx, so a label, an icon or a
 * capability changes in one place and both menus follow.
 *
 * More rises as a sheet holding every other screen the rail offers, grouped as the rail
 * groups them. The classic's More held two screens; this console has more than fit in a
 * row of tabs, and the phone no longer shows the drawer they would otherwise be in.
 *
 * As in the classic, the bar slides away while the page is scrolled down and comes back
 * on the way up. It also steps aside while a text field has the keyboard up, where it
 * would otherwise ride up on top of the keyboard and cover the field.
 */
import { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';

import { navGroups } from '../navigation';
import { roleCan } from '../capabilities';
import { Sheet } from './Overlay';
import { IconMore } from './Icons';

/* The classic's tabs, in its order. A tab the role cannot open is left out. */
const DOCK_IDS = ['/home', '/leads', '/clinics', '/templates', '/users', '/traffic-drop'];

const railGroups = (role) => navGroups
  .map((g) => ({
    group: g.group,
    items: g.items.filter((i) => !i.hidden && roleCan(role, i.cap) && !(i.hideFor && roleCan(role, i.hideFor)))
  }))
  .filter((g) => g.items.length);

/* A tab stays lit on the screens under it: a lead under Leads, a clinic under Clinics. */
const isUnder = (id, pathname, back) => pathname === id || pathname.startsWith(`${id}/`) || back === id;

const TEXT_FIELD = /^(INPUT|TEXTAREA)$/;
const NOT_TYPED = /^(checkbox|radio|button|submit|reset|range|file|color)$/;

/**
 * True while the bar should be out of the way: scrolled down, or typing. Held by the
 * shell rather than the bar, because the selection tray on the lead list moves with it.
 */
export const useDockHidden = (enabled, pathname) => {
  const [down, setDown] = useState(false);
  const [typing, setTyping] = useState(false);

  /* Every screen opens with the bar showing. */
  useEffect(() => { setDown(false); }, [pathname]);

  useEffect(() => {
    if (!enabled) return undefined;
    let last = Math.max(0, window.scrollY || 0);
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const y = Math.max(0, window.scrollY || 0);
        if (Math.abs(y - last) < 8) return;
        /* Back at the very end of the page it returns, as it does at the top. */
        const atEnd = window.innerHeight + y >= document.documentElement.scrollHeight - 4;
        setDown(y > last && y > 64 && !atEnd);
        last = y;
      });
    };
    const onFocusIn = (e) => {
      const el = e.target;
      setTyping(!!el && (el.isContentEditable || (TEXT_FIELD.test(el.tagName) && !NOT_TYPED.test(el.type || ''))));
    };
    const onFocusOut = () => setTyping(false);

    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [enabled]);

  return enabled && (down || typing);
};

const Count = ({ n }) => (n ? <span className="pip">{n > 99 ? '99+' : n}</span> : null);

const BottomNav = ({ role, pathname, back, badgeFor, hidden, onGo }) => {
  const [more, setMore] = useState(false);

  const groups = useMemo(() => railGroups(role), [role]);
  const tabs = useMemo(() => {
    const all = groups.reduce((out, g) => out.concat(g.items), []);
    return DOCK_IDS.map((id) => all.find((i) => i.id === id)).filter(Boolean);
  }, [groups]);
  const rest = useMemo(() => groups
    .map((g) => ({ group: g.group, items: g.items.filter((i) => DOCK_IDS.indexOf(i.id) < 0) }))
    .filter((g) => g.items.length), [groups]);

  /* Back, or a link, can change the screen with the sheet still up. */
  useEffect(() => { setMore(false); }, [pathname]);

  const current = tabs.find((t) => isUnder(t.id, pathname, back));
  const inMore = !current && rest.some((g) => g.items.some((i) => isUnder(i.id, pathname, back)));
  const moreHasCount = rest.some((g) => g.items.some((i) => badgeFor(i.id)));

  return (
    <>
      <nav className={`nt-dock${hidden && !more ? ' down' : ''}`} aria-label="Main menu">
        {tabs.map((t) => {
          const Icon = t.icon;
          const on = current === t;
          return (
            <NavLink
              key={t.id}
              to={t.id}
              className={`nt-dock-tab${on ? ' on' : ''}`}
              aria-current={on ? 'page' : undefined}
              /* Tapping the tab you are already on goes back to the top, as apps do. */
              onClick={(e) => {
                if (pathname !== t.id) return;
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <span className="ic"><Icon /><Count n={badgeFor(t.id)} /></span>
              <span className="lb">{t.label}</span>
            </NavLink>
          );
        })}

        {rest.length ? (
          <button
            type="button"
            className={`nt-dock-tab nt-dock-more${inMore || more ? ' on' : ''}`}
            aria-haspopup="dialog"
            aria-expanded={more}
            onClick={() => setMore(true)}
          >
            <span className="ic"><IconMore />{moreHasCount ? <span className="dot" /> : null}</span>
            <span className="lb">More</span>
          </button>
        ) : null}
      </nav>

      {more ? (
        <Sheet title="More" onClose={() => setMore(false)}>
          {rest.map((g) => (
            <section key={g.group} className="nt-more-sec" aria-label={g.group}>
              <div className="nt-more-label">{g.group}</div>
              <div className="nt-more-grid">
                {g.items.map((i) => {
                  const Icon = i.icon;
                  const on = isUnder(i.id, pathname, back);
                  return (
                    <button
                      key={i.id}
                      type="button"
                      className={`nt-more-tile${on ? ' on' : ''}`}
                      aria-current={on ? 'page' : undefined}
                      onClick={() => { setMore(false); onGo(i.id); }}
                    >
                      <span className="ic"><Icon /><Count n={badgeFor(i.id)} /></span>
                      <span className="lb">{i.label}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </Sheet>
      ) : null}
    </>
  );
};

export default BottomNav;
